import { Ask } from '../classes/Ask/ask.service';
import { Field } from '../classes/Field/field';
import { LoggerService } from './logger.service';

/**
 * Classe utilitaire statique pour les opérations de filtrage du composant Selector
 * Contient toutes les méthodes de filtrage, visibilité et gestion hiérarchique
 */
export class SelectorUtils {
    /**
     * Vérifie si un élément est visible en fonction de ses liens (linkedTo)
     * @param data - Données de l'élément à vérifier
     * @param currentAsk - Instance Ask actuelle pour récupérer les champs
     * @returns true si l'élément est visible, false sinon
     */
    static isVisible(data: any, currentAsk: Ask): boolean {
        if (data.linkedTo) {
            for (const [TargetField, value] of Object.entries(data.linkedTo)) {
                if (value == 0) continue;
                const field: Field = currentAsk.getField({ TargetField: TargetField });
                if (!field) continue;
                if (field.orginialValue?.toString() != value?.toString()) return false;
            }
        }
        return true;
    }

    /**
     * Vérifie si un élément a des enfants dans la hiérarchie
     * @param contents - Liste de tous les contenus
     * @param selected - Valeur sélectionnée当前值
     * @returns true si l'élément a des enfants, false sinon
     */
    static hasChildren(contents: Array<any>, selected: number): boolean {
        if (selected == 0) return false;
        return contents.some(data => data.parentValue == selected);
    }

    /**
     * Vérifie si un contenu spécifique a des enfants
     * @param contents - Liste de tous les contenus
     * @param content - Contenu à vérifier
     * @returns true si le contenu a des enfants, false sinon
     */
    static gotChild(contents: Array<any>, content: any): boolean {
        return contents.some(data => data.parentValue == content.value);
    }

    /**
     * Désactive tous les éléments qui ne sont pas dans la hiérarchie sélectionnée
     * @param contents - Liste de tous les contenus
     * @param lowestLevel - Niveau le plus bas autorisé
     * @param currentLevel - Niveau actuel (par défaut 0)
     */
    static disableOutOfHierarchy(contents: Array<any>, lowestLevel: number, currentLevel: number = 0): void {
        if (currentLevel == lowestLevel) return;
        for (const level of contents.filter(data => data.parentValue == currentLevel)) {
            level.locked = true;
            SelectorUtils.disableOutOfHierarchy(contents, lowestLevel, level.value);
        }
    }

    /**
     * Calcule le label parent complet pour un élément
     * @param contents - Liste de tous les contenus
     * @param selected - Valeur sélectionnée
     * @param current - Contenu actuel
     * @returns Label parent complet
     */
    static getFullParentLabel(contents: Array<any>, selected: number, current: any): string {
        if (!SelectorUtils.hasChildren(contents, selected) && !current?.parentValue) return "";
        if (!SelectorUtils.hasChildren(contents, selected) && current?.parentValue) {
            return current.fullParentLabel;
        }
        return (current.fullParentLabel ? current.fullParentLabel + " " : "") + current.label + " /";
    }

    /**
     * Filtre les contenus selon les critères de visibilité, hiérarchie et texte
     * @param contents - Liste de tous les contenus
     * @param filter - Texte de filtre (optionnel)
     * @param selected - Valeur sélectionnée
     * @param lowestLevel - Niveau le plus bas autorisé
     * @param currentAsk - Instance Ask actuelle
     * @returns Liste des contenus filtrés
     */
    static filterContents(
        contents: Array<any>,
        filter: string,
        selected: number,
        lowestLevel: number,
        currentAsk: Ask
    ): Array<any> {
        LoggerService.log('Début du filtrage des contenus');

        // Filtrage par visibilité (linkedTo)
        const filteredContents = contents.filter(data => SelectorUtils.isVisible(data, currentAsk));

        let result: Array<any>;

        if (filter === "") {
            // Filtrage par hiérarchie
            const hasChildren = SelectorUtils.hasChildren(contents, selected);
            const current = contents.find((data) => data.value == selected);

            result = filteredContents.filter(data => {
                if (hasChildren) {
                    return data.parentValue == selected;
                } else {
                    return (!data.parentValue && selected == 0) || data.parentValue == current?.parentValue;
                }
            });
        } else {
            // Filtrage par texte
            const fullParentLabel = SelectorUtils.getFullParentLabel(contents, selected, contents.find((data) => data.value == selected));
            const fullParentLabelLowered = fullParentLabel.toLowerCase();
            const filterLowered = filter.toLowerCase();

            result = filteredContents.filter(data => {
                if (!data.label.toLowerCase().includes(filterLowered)) return false;
                const parentLabelToCheck = (data.fullParentLabel + " " + data.label).toLowerCase();
                return parentLabelToCheck.includes(filterLowered) && parentLabelToCheck.includes(fullParentLabelLowered);
            });
        }

        LoggerService.log(`Fin du filtrage des contenus: ${result.length} éléments trouvés`);
        return result;
    }

    /**
     * Calcule le style CSS pour un élément en fonction de sa position hiérarchique
     * @param contents - Liste de tous les contenus
     * @param selected - Valeur sélectionnée
     * @param current - Contenu actuel
     * @returns Chaîne CSS pour le style
     */
    static getCssStyle(contents: Array<any>, selected: number, current: any): string {
        const hasChildren = SelectorUtils.hasChildren(contents, selected);
        return hasChildren || current?.parentValue ? "margin-left: 38px;width: calc(100% - 38px);" : "";
    }
}